import { DatePicker, Form, Input, Modal, Select } from 'antd';
import moment, { Moment } from 'moment';
import { FC, useMemo } from 'react';

import {
  RECRUITMENT_STATUS,
  RECRUITMENT_STATUS_LABELS,
  useUpdateWorkerInPlan,
} from '@/modules/recruitment-organization/hooks/useUpdateWorkerInPlan';

interface IProps {
  readonly planId: string;
  readonly workerId: string;
  readonly currentStatus?: string;
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onSaved?: (newStatus?: string) => void;
}

// V1.4 B3 — work_start_date required when target = CD_ARRIVED. Date range
// constrained to today..today+30d per spec §4.6 T-TCTD-03 DoD.
const STATUS_REQUIRES_DATE = RECRUITMENT_STATUS.ARRIVED;

// FSM map mirrored from backend status_fsm.go — keeps UI from offering
// invalid transitions so users don't hit 422 after submit.
// For Bug 110: Allow all transitions to fix real-world mistakes.
const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  '*': Object.values(RECRUITMENT_STATUS)
};

const UpdateWorkerStatusModal: FC<IProps> = ({
  planId,
  workerId,
  currentStatus,
  visible,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const mutation = useUpdateWorkerInPlan({ onSuccess: (_, variables) => { onSaved?.(variables.recruitment_status_id); onClose(); } });

  const targetStatus = Form.useWatch('recruitment_status_id', form);
  const showWorkStartDate = targetStatus === STATUS_REQUIRES_DATE;

  const options = useMemo(() => {
    // For Bug 110: Allow all transitions to fix real-world mistakes
    const allowed = ALLOWED_TRANSITIONS['*'] || [];

    return allowed.map((code) => ({
      label: RECRUITMENT_STATUS_LABELS[code] ?? code,
      value: code,
    }));
  }, [currentStatus]);

  function disabledDate(d: Moment) {
    if (!d) return false;
    const today = moment().startOf('day');
    const max = today.clone().add(30, 'day');

    return d.isBefore(today) || d.isAfter(max);
  }

  async function handleOk() {
    const values = await form.validateFields();
    const payload = {
      plan_id: planId,
      worker_id: workerId,
      recruitment_status_id: values.recruitment_status_id,
      note: values.note,
      work_start_date:
        values.recruitment_status_id === STATUS_REQUIRES_DATE && values.work_start_date
          ? (values.work_start_date as Moment).format('YYYY-MM-DD')
          : undefined,
    };
    mutation.mutate(payload);
  }

  return (
    <Modal
      cancelText="Huỷ"
      confirmLoading={mutation.isLoading}
      okText="Lưu"
      onCancel={onClose}
      onOk={handleOk}
      open={visible}
      title="Cập nhật trạng thái NLĐ trong chiến dịch"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Trạng thái mới"
          name="recruitment_status_id"
          rules={[{ message: 'Vui lòng chọn trạng thái', required: true }]}
        >
          <Select options={options} placeholder="Chọn trạng thái đích..." />
        </Form.Item>

        {showWorkStartDate && (
          <Form.Item
            extra="V1.4 B3: Ngày bắt đầu chính thức làm việc. Lương + chấm công tính từ ngày này."
            label="Ngày bắt đầu làm việc (work_start_date)"
            name="work_start_date"
            rules={[{ message: 'Vui lòng chọn ngày bắt đầu', required: true }]}
          >
            <DatePicker
              disabledDate={disabledDate}
              format="DD/MM/YYYY"
              placeholder="Chọn ngày..."
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Form.Item label="Ghi chú" name="note">
          <Input.TextArea placeholder="Tuỳ chọn..." rows={3} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export { UpdateWorkerStatusModal };
